import { type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
    Box,
    Button,
    CardContent,
    CardHeader,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Snackbar,
    Stack,
    styled,
    Tab,
    Tabs
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { type LtiRoutesParams } from './ClassroomRoutes';
import { sessionOpened } from './SessionsTable';
import { type ClassRecording } from './types';
import { getGroupLabelsForSession } from './util';
import { CREATE_LTI_CODE, GET_CLASS_USER_SESSION_DATA_PATH } from '../../config/apiPaths';
import { AttendanceProvider, View } from '../../context/AttendanceContext';
import { ClassContext } from '../../context/ClassContext';
import { type GroupSectionLabel, Roles } from '../../context/types';
import { useRecordings } from '../../hooks/useRecordings';
import {
    ClassAccess,
    type ClassLevelOptions,
    ClassLevelOverrideOptions,
    type ClassOptionsOverrides,
    type SchoolLevelOptions
} from '../../types/adminConfig';
import { type ClassUserSessionData } from '../../types/apiTypes';
import { copyToClipboard } from '../../utils/clipboard';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrlParams, getUrls, processApiPath } from '../../utils/url';
import AttendanceView from '../attendance/AttendanceView';
import { CardStyled } from '../common/collapsible/CollapsibleCard';
import UserState from '../common/userState/UserState';
import AttendanceLayout from '../layout/AttendanceLayout';
import MainLayout, { type SubTitleItem } from '../layout/MainLayout';
import { Recordings } from '../recordings/Recordings';
import { TabsPanel } from '../templates/Tables/TemplatesTabsPanel';

interface ClassroomSessiontProps {
    classSessionUuid: string;
}

const ButtonUnderline = styled(Button)`
    text-decoration: underline;
` as typeof Button;

const SessionDetailsContainer = styled(Box)`
    margin-bottom: 20px;
` as typeof Box;

const SessionControls = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 8px;
` as typeof Stack;

const TabContext = styled(Box)`
    width: 100%;
` as typeof Box;

const TabsStyled = styled(Tabs)`
    & .MuiTabs-flexContainer {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        justify-content: space-evenly;
    }

    & .MuiButtonBase-root {
        flex-grow: 1;
        max-width: 600px;
        text-transform: none;
        font-size: 15px;
    }
` as typeof Tabs;

enum TabNames {
    Recordings,
    Notes,
    Chats,
    Attendance
}

export const ClassroomSessionContent = (props: PropsWithChildren<ClassroomSessiontProps>) => {
    const { classSessionUuid } = useParams<LtiRoutesParams>();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const { api, vanityUrl } = getUrls();
    const { t } = useTranslation(['class', 'lti', 'common', 'admin_settings']);
    const {
        me,
        meFetched,
        adminSettings,
        classData,
        sessions,
        userUuid,
        classUuid,
        isLoadingClassData,
        mergedClassSettings: classSettings
    } = useContext(ClassContext);

    const meetingId = classData.class.meeting_id;
    const { role, user_name: username } = me;
    const [isLaunching, setIsLaunching] = useState(false);
    const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);
    const isInstructor = useMemo(() => role === Roles.Teacher, [role]);
    const isAssistant = useMemo(() => role === Roles.Assistant, [role]);
    const isPrivilegedUser = useMemo(() => isInstructor || isAssistant, [isAssistant, isInstructor]);
    const { class: classInfo, classGroups, userGroups } = classData;
    const { sid: classId } = classInfo;
    const [classUserSessionData, setClassUserSessionData] = useState<ClassUserSessionData>({} as ClassUserSessionData);
    const [isLoadingClassUserSessionData, setIsLoadingClassUserSessionData] = useState(true);
    const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
    const [sessionEndTime, setSessionEndTime] = useState<Date | null>(null);
    const [tab, setTab] = useState<TabNames>(TabNames.Recordings);
    const [recordings, setRecordings] = useState<ClassRecording[]>([]);

    useEffect(() => {
        const fetchClassUserSessionData = async () => {
            if (!classId || !classSessionUuid || !userUuid) {
                return;
            }
            setIsLoadingClassUserSessionData(true);
            try {
                const { data } = await axios.get(
                    processApiPath(GET_CLASS_USER_SESSION_DATA_PATH, {
                        classId: String(classId),
                        sessionUuid: classSessionUuid
                    }),
                    { headers: getUserUuidHeaders(userUuid) }
                );
                setClassUserSessionData(data);
            } catch (error) {
                enqueueSnackbar(t('class:error-message'), { variant: 'error' });
            } finally {
                setIsLoadingClassUserSessionData(false);
            }
        };
        if (classId && classSessionUuid && userUuid && meFetched && me !== undefined) {
            fetchClassUserSessionData();
        }
    }, [classId, classSessionUuid, enqueueSnackbar, t, userUuid, meFetched, me]);

    const { from_scheduler: fromScheduler } = getUrlParams();
    const [groupSectionLabels, setGroupSectionLabels] = useState<GroupSectionLabel[]>([]);

    const subTitles: SubTitleItem[] = useMemo(() => {
        const result: SubTitleItem[] = [];
        if (classUserSessionData) {
            const startDate = new Date(Number(classUserSessionData.start_time) * 1000);
            const endDate = new Date(Number(classUserSessionData.end_time) * 1000);
            setSessionStartTime(startDate);
            setSessionEndTime(endDate);
            const session = sessions.find((session) => session.class_session_uuid === classSessionUuid);
            if (session?.class_session_group) {
                const labels = getGroupLabelsForSession(session, classGroups, userGroups, !fromScheduler, me.role);
                setGroupSectionLabels(labels);
                if (labels.length === 1) {
                    let sectionLabel = labels[0].label;
                    if (labels[0].section === 'all') {
                        sectionLabel = `${sectionLabel} ${t('class:all-sections')}`;
                    } else if (labels[0].section) {
                        sectionLabel = `${sectionLabel} ${labels[0].section}`;
                    }
                    if (classUserSessionData.class_session_topic) {
                        result.push({ title: classUserSessionData.class_session_topic });
                        result.push({ title: sectionLabel });
                    } else {
                        result.push({ title: sectionLabel });
                        result.push({ title: `${startDate.toLocaleString()} - ${endDate.toLocaleTimeString()}` });
                    }
                }
            }
            if (result.length === 0) {
                if (classUserSessionData.class_session_topic) {
                    result.push({ title: classUserSessionData.class_session_topic });
                } else {
                    result.push({ title: `${startDate.toLocaleString()} - ${endDate.toLocaleTimeString()}` });
                }
            }
        }
        return result;
    }, [classGroups, classSessionUuid, classUserSessionData, fromScheduler, me.role, sessions, t, userGroups]);

    // Super admin setting for when these tabs a visible to users
    const canAccess = useCallback(
        (access: ClassAccess | undefined): boolean => {
            if (!access || access === ClassAccess.None) {
                return false;
            }

            if (access === ClassAccess.All) {
                return true;
            }

            return fromScheduler ? access === ClassAccess.App : access === ClassAccess.Lti;
        },
        [fromScheduler]
    );
    const canSeeAttendance = isPrivilegedUser && canAccess(adminSettings?.attendance_tab_access);

    const canSeeRecordings =
        (isPrivilegedUser && fromScheduler === '2') ||
        ((isPrivilegedUser || adminSettings?.recording_participants_can_view) &&
            canAccess(adminSettings?.recording_tab_access));
    const hasTopic = classUserSessionData?.class_session_topic && classUserSessionData.class_session_topic != '';
    const hasGroupSectionLabels = groupSectionLabels.length > 0;
    const {
        refetchRecordings,
        data: recordingsFromRequest,
        isLoadingRecordings,
        isFetchingRecordings
    } = useRecordings({
        userUuid,
        classUuid,
        meetingId,
        showLegacyRecordings: false,
        doFetch: tab == TabNames.Recordings
    });

    useEffect(() => {
        if (!recordingsFromRequest) {
            return;
        }
        setRecordings(recordingsFromRequest);
    }, [recordingsFromRequest]);

    const showJoinClassRoomButton = useMemo(
        () => !!isPrivilegedUser || sessionOpened(classSettings, isPrivilegedUser, classUserSessionData),
        [classUserSessionData, classSettings, isPrivilegedUser]
    );

    const autoJoinWhenSessionActive = useMemo(
        () =>
            sessionOpened(classSettings, isPrivilegedUser, classUserSessionData) &&
            adminSettings?.options.autoJoinWhenSessionActive,
        [classSettings, isPrivilegedUser, classUserSessionData, adminSettings?.options.autoJoinWhenSessionActive]
    );
    const defaultWebExperience = 'nextgen';
    const getWebExperience = (
        orgOptions?: SchoolLevelOptions,
        classOptions?: ClassLevelOptions,
        overrides?: ClassOptionsOverrides
    ) => {
        if (!overrides?.webExperience || overrides.webExperience === ClassLevelOverrideOptions.NoOne) {
            return orgOptions?.webExperience ?? defaultWebExperience;
        }
        return classOptions?.webExperience ?? orgOptions?.webExperience ?? defaultWebExperience;
    };
    const onClickClassPage = () => {
        if (!searchParams.has('skipHub')) {
            searchParams.set('skipHub', 'true');
        }
        window.location.href = `/react/lti/${classUuid}?${searchParams?.toString()}`;
    };
    const launch = useCallback(async () => {
        const webExperience = getWebExperience(
            adminSettings?.options,
            classData.class.class_options,
            adminSettings?.overrides ?? {}
        );
        if (fromScheduler) {
            // This page was accessed via the scheduler, so we need to launch differently.
            // Pass a request to join the class up to the parent window.
            window.parent.postMessage(
                {
                    func: 'request_join_class',
                    event: {
                        meeting_id: meetingId,
                        password: classData.class.password,
                        user_uuid: userUuid,
                        meeting_provider: classData.class.meeting_provider,
                        webExperience,
                        username
                    }
                },
                '*'
            );

            return;
        }

        setIsLaunching(true);

        try {
            const response = await axios.post(
                `${api}${CREATE_LTI_CODE}`,
                { student_id: me.student_id },
                { headers: { token: userUuid } }
            );

            const ltiCode = response.data.code;
            let url = `${window.location.protocol}//${window.location.host}/class/${classUuid}`;

            const params = new URLSearchParams({
                code: ltiCode,
                is_lti: 'true',
                username: username ?? ''
            });
            url += `?${params}`;

            window.open(url, 'Class');
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t('class:error-launch'), { variant: 'error' });
        }

        setIsLaunching(false);
    }, [
        adminSettings?.options,
        adminSettings?.overrides,
        api,
        classData.class.class_options,
        classData.class.meeting_provider,
        classData.class.password,
        classUuid,
        enqueueSnackbar,
        fromScheduler,
        me.student_id,
        meetingId,
        t,
        userUuid,
        username
    ]);

    useEffect(() => {
        if (me && meFetched && autoJoinWhenSessionActive) {
            launch();
        }
    }, [autoJoinWhenSessionActive, me, meFetched, launch]);

    const onClickCopyInviteLink = async () => {
        await copyToClipboard(`${vanityUrl}/class/${classUuid}`);
        setShowCopyConfirmation(true);
    };

    const onCloseCopyLinkClickHandler = () => {
        setShowCopyConfirmation(false);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    return (
        <MainLayout
            title={classData.class.class_name}
            titleOnClick={onClickClassPage}
            subTitles={subTitles}
            loading={isLoadingClassData || isLoadingClassUserSessionData}
            narrowStyle={false}
            controls={[<UserState name={me.user_name} email={me.email} avatarUrl={me.user_image} />]}
        >
            <SessionDetailsContainer>
                <SessionControls>
                    {showJoinClassRoomButton && (
                        <>
                            <Button
                                size="medium"
                                color="primary"
                                variant="contained"
                                disabled={!meFetched}
                                onClick={launch}
                            >
                                {isLaunching ? t('class:launching') : t('lti:join-class-room')}
                            </Button>
                            {isInstructor && (
                                <>
                                    <ButtonUnderline
                                        variant="text"
                                        disabled={!classUuid}
                                        onClick={onClickCopyInviteLink}
                                    >
                                        {t('lti:copy-guest-invite-link')}
                                    </ButtonUnderline>
                                    <Snackbar
                                        open={showCopyConfirmation}
                                        autoHideDuration={3000}
                                        onClose={onCloseCopyLinkClickHandler}
                                        message={t('common:copied')}
                                    />
                                </>
                            )}
                        </>
                    )}
                </SessionControls>
                <CardStyled>
                    <CardHeader title={t('class:session-details')} />
                    <CardContent sx={{ paddingTop: 0 }}>
                        <List>
                            {hasTopic && (
                                <ListItem key={'session-details-topic'} disablePadding>
                                    <ListItemText
                                        primary={classUserSessionData?.class_session_topic}
                                        secondary={t('session-topic')}
                                        key={'session-details-topic-text'}
                                    />
                                </ListItem>
                            )}
                            {hasGroupSectionLabels && (
                                <ListItem key={'session-details-sections'} disablePadding>
                                    <ListItemText
                                        primary={groupSectionLabels
                                            .map((label) =>
                                                label.section
                                                    ? label.section === 'all'
                                                        ? t('class:all-sections')
                                                        : label.section
                                                    : label.label
                                            )
                                            .join(', ')}
                                        secondary={
                                            groupSectionLabels.length === 1 ? t('class:section') : t('class:sections')
                                        }
                                        key={'session-details-sections-text'}
                                    />
                                </ListItem>
                            )}
                            {sessionStartTime && (
                                <ListItem key={'session-details-start-time'} disablePadding>
                                    <ListItemText
                                        primary={sessionStartTime ? sessionStartTime.toLocaleString() : ''}
                                        secondary={t('session-start-time')}
                                        key={'session-details-start-time-text'}
                                    />
                                </ListItem>
                            )}
                            {sessionEndTime && (
                                <ListItem key={'session-details-end-time'} disablePadding>
                                    <ListItemText
                                        primary={sessionEndTime ? sessionEndTime.toLocaleString() : ''}
                                        secondary={t('session-end-time')}
                                        key={'session-details-end-time-text'}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </CardContent>
                </CardStyled>
            </SessionDetailsContainer>
            <TabContext>
                <TabsStyled value={tab} onChange={handleTabChange}>
                    {canSeeRecordings && (
                        <Tab
                            label={
                                <Box position="relative">
                                    {t('class:recordings')}
                                    {tab === TabNames.Recordings && isFetchingRecordings && (
                                        <CircularProgress
                                            sx={{
                                                position: 'absolute',
                                                top: 'calc(50% - 8px)',
                                                right: '-20px'
                                            }}
                                            size={16}
                                        />
                                    )}
                                </Box>
                            }
                            value={TabNames.Recordings}
                        />
                    )}
                    {canSeeAttendance && (
                        <Tab value={TabNames.Attendance} key="attendance-tab" label={t('class:attendance')} />
                    )}
                </TabsStyled>
                {canSeeRecordings && (
                    <TabsPanel value={tab} index={TabNames.Recordings} key="recordings-tabpanel">
                        <Recordings
                            recordings={recordings}
                            setRecordings={setRecordings}
                            showLegacyRecordings={false}
                            isFetchingRecordings={isFetchingRecordings}
                            isLoadingRecordings={isLoadingRecordings}
                            refetchRecordings={refetchRecordings}
                        />
                    </TabsPanel>
                )}

                {canSeeAttendance && (
                    <TabsPanel value={tab} index={TabNames.Attendance}>
                        {!classInfo?.class_uuid && <CircularProgress />}

                        {classInfo?.class_uuid && (
                            <AttendanceProvider
                                classId={classInfo.class_uuid}
                                classSessionId={classSessionUuid}
                                view={View.SessionSummary}
                            >
                                <AttendanceLayout>
                                    <AttendanceView />
                                </AttendanceLayout>
                            </AttendanceProvider>
                        )}
                    </TabsPanel>
                )}
            </TabContext>
        </MainLayout>
    );
};
